import { Truck } from "lucide-react";
import { useTranslations } from "next-intl";

type Props = {
	/** Already-formatted shortfall amount, e.g. "12,50 €" or "4.875 HUF". */
	shortfallLabel: string;
	/** Already-formatted threshold amount, e.g. "50,00 €". */
	thresholdLabel: string;
	/** Subtotal as a fraction of the threshold (0..1). */
	progress: number;
	/** Layout density. "inline" matches the cart-drawer's compact bar; "card" matches the cart-page's framed banner. */
	variant?: "inline" | "card";
};

export function CartMovBanner({ shortfallLabel, thresholdLabel, progress, variant = "inline" }: Props) {
	const t = useTranslations("cart");
	const clamped = Math.min(Math.max(progress, 0), 1);

	if (variant === "card") {
		return (
			<div className="mb-8 rounded-lg border border-info-border bg-info p-4">
				<div className="mb-2 flex items-center gap-2 text-sm font-medium text-info-foreground">
					<Truck className="h-4 w-4" />
					<span>{t("minimumOrderProgress", { amount: shortfallLabel })}</span>
				</div>
				<div
					className="h-2 overflow-hidden rounded-full bg-secondary"
					role="progressbar"
					aria-valuenow={Math.round(clamped * 100)}
					aria-valuemin={0}
					aria-valuemax={100}
					aria-label={t("minimumOrderProgress", { amount: shortfallLabel })}
				>
					<div
						className="h-full rounded-full bg-foreground transition-all duration-500 ease-out"
						style={{ width: `${clamped * 100}%` }}
					/>
				</div>
				<p className="mt-2 text-xs text-muted-foreground">
					{t("minimumOrderHint", { amount: thresholdLabel })}
				</p>
			</div>
		);
	}

	return (
		<div className="grid grid-cols-[auto_1fr] items-start gap-2 text-sm font-medium text-info-foreground">
			<Truck className="h-3.5 w-3.5 shrink-0" />
			<span className="min-w-0">{t("minimumOrderProgress", { amount: shortfallLabel })}</span>
			<div
				className="col-span-2 h-1 w-full overflow-hidden rounded-full bg-border"
				role="progressbar"
				aria-valuenow={Math.round(clamped * 100)}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-label={t("minimumOrderProgress", { amount: shortfallLabel })}
			>
				<div
					className="h-full rounded-full bg-foreground transition-all duration-500 ease-out"
					style={{ width: `${clamped * 100}%` }}
				/>
			</div>
		</div>
	);
}
